import { useEffect, useState } from 'react';

/**
 * The masonry's one input from the DOM. A callback ref rather than a
 * `RefObject`, because the element it watches only mounts once the room has
 * loaded — an effect keyed on a ref object would have run against `null` and
 * never fired again.
 *
 * Debounced: a window drag fires this dozens of times a second, and relaying
 * out a few hundred tiles on each is what makes a homemade grid feel cheap.
 * The tiles animate to their new columns once the drag settles instead.
 */
export function useContainerWidth(debounceMs = 80): [(node: HTMLElement | null) => void, number] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (node === null) return;

    setWidth(node.getBoundingClientRect().width);

    let timer: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;

      const next = entry.contentRect.width;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setWidth(next), debounceMs);
    });

    observer.observe(node);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [node, debounceMs]);

  return [setNode, width];
}
